"use client";

import { CHART_SERIES } from "@ui/lib/chart-palette";
import { cn } from "@ui/lib";
import type { CSSProperties } from "react";
import { CountUp, useRevealed } from "./motion";

/**
 * Token-clean circular score for marketing tiles: brand chart slot for the
 * arc, text tokens for the value, CSS transition for the sweep. Replaces the
 * legacy report CircularScore (hardcoded hex) in mockups; graduating this in
 * Phase B retires that one.
 */
export function ScoreRing({
	score,
	size = 112,
	label,
	color = CHART_SERIES[0],
	className,
}: {
	score: number;
	size?: number;
	label?: string;
	color?: string;
	className?: string;
}) {
	const revealed = useRevealed();
	const r = 40;
	const circumference = 2 * Math.PI * r;
	const offset = circumference * (1 - (revealed ? score : 0) / 100);

	return (
		<div
			className={cn("flex flex-col items-center gap-2", className)}
			style={{ width: size }}
		>
			<div className="relative" style={{ width: size, height: size }}>
				<svg
					viewBox="0 0 100 100"
					className="h-full w-full -rotate-90"
					aria-hidden
				>
					<circle
						cx="50"
						cy="50"
						r={r}
						fill="none"
						strokeWidth="9"
						className="stroke-muted"
					/>
					<circle
						cx="50"
						cy="50"
						r={r}
						fill="none"
						strokeWidth="9"
						strokeLinecap="round"
						stroke={color}
						strokeDasharray={circumference}
						strokeDashoffset={offset}
						className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-[1200ms] motion-safe:ease-out motion-reduce:[stroke-dashoffset:var(--final-offset)]"
						style={
							{
								"--final-offset": circumference * (1 - score / 100),
							} as CSSProperties
						}
					/>
				</svg>
				<span className="absolute inset-0 flex items-center justify-center font-mono text-2xl font-extrabold">
					<CountUp value={score} />
				</span>
			</div>
			{label ? (
				<span className="text-center text-xs font-medium text-muted-foreground">
					{label}
				</span>
			) : null}
		</div>
	);
}
